import { FilterCreatorsDto } from '@/app/common/general-types/filters.dto';
import { ScreeningQuestionTypeEnum } from '@/app/common/general-types/briefs.dto';
import {
  hasActiveScreeningFilters,
  ScreeningFilterMap,
} from '@/app/pages/manage-brief-tabs/service/manage-brief-tabs/applicant-profile-filter.helpers';
import {
  emptyPlatformPerformanceDraft,
  isDefaultPerformanceRange,
  PERFORMANCE_RANGE_DEFAULT,
  PerformanceRangeValue,
  PlatformPerformanceDraft,
} from '@/app/common/app-config/filter-models/performance-range';

export type { ScreeningFilterMap };
export { hasActiveScreeningFilters };

export interface AssignmentFilterDraft {
  country: string | null;
  gender: number | null;
  rating: number | null;
  ageFrom: number | null;
  ageTo: number | null;
  isVerified: boolean;
  hasReviews: boolean;
  tikTokFollowersFrom: number | null;
  instagramFollowersFrom: number | null;
  tikTokPerformance: PlatformPerformanceDraft;
  instagramPerformance: PlatformPerformanceDraft;
  youtubePerformance: PlatformPerformanceDraft;
  screening: ScreeningFilterMap;
}

export const EMPTY_ASSIGNMENT_FILTER_DRAFT: AssignmentFilterDraft = {
  country: null,
  gender: null,
  rating: null,
  ageFrom: null,
  ageTo: null,
  isVerified: false,
  hasReviews: false,
  tikTokFollowersFrom: null,
  instagramFollowersFrom: null,
  tikTokPerformance: emptyPlatformPerformanceDraft(),
  instagramPerformance: emptyPlatformPerformanceDraft(),
  youtubePerformance: emptyPlatformPerformanceDraft(),
  screening: {},
};

export function emptyAssignmentFilterDraft(): AssignmentFilterDraft {
  return {
    ...EMPTY_ASSIGNMENT_FILTER_DRAFT,
    screening: {},
    tikTokPerformance: emptyPlatformPerformanceDraft(),
    instagramPerformance: emptyPlatformPerformanceDraft(),
    youtubePerformance: emptyPlatformPerformanceDraft(),
  };
}

export function screeningChipKey(questionId: string): `screening:${string}` {
  return `screening:${questionId}`;
}

export function parseScreeningChipKey(key: string): string | null {
  if (!key.startsWith('screening:')) {
    return null;
  }
  return key.slice('screening:'.length) || null;
}

export function screeningFilterValueLabel(value: string): string {
  switch (value) {
    case 'pass':
      return 'Pass';
    case 'fail':
      return 'Fail';
    case 'answered':
      return 'Answered';
    case 'Yes':
      return 'Yes';
    case 'No':
      return 'No';
    default:
      return value;
  }
}

export function assignmentDraftToDto(
  draft: AssignmentFilterDraft,
  isSocialBrief: boolean,
): FilterCreatorsDto {
  const dto: FilterCreatorsDto = {};

  if (draft.country) {
    dto.country = [draft.country];
  }
  if (draft.gender != null) {
    dto.gender = [draft.gender];
  }
  if (draft.rating != null) {
    dto.rating = draft.rating;
  }
  if (draft.ageFrom != null) {
    dto.ageFrom = draft.ageFrom;
  }
  if (draft.ageTo != null) {
    dto.ageTo = draft.ageTo;
  }
  if (draft.isVerified) {
    dto.isVerified = true;
  }
  if (draft.hasReviews) {
    dto.hasReviews = true;
  }
  if (isSocialBrief) {
    if (draft.tikTokFollowersFrom != null) {
      dto.tikTokFollowersFrom = draft.tikTokFollowersFrom;
    }
    if (draft.instagramFollowersFrom != null) {
      dto.instagramFollowersFrom = draft.instagramFollowersFrom;
    }
  }

  applyAssignmentPerformanceToDto(dto, draft.tikTokPerformance, 'tikTok');
  applyAssignmentPerformanceToDto(dto, draft.instagramPerformance, 'instagram');
  applyAssignmentPerformanceToDto(dto, draft.youtubePerformance, 'youtube');

  return dto;
}

export function assignmentDtoToDraft(
  filters: FilterCreatorsDto,
  screening: ScreeningFilterMap = {},
): AssignmentFilterDraft {
  const genderRaw = filters.gender;
  let gender: number | null = null;
  if (genderRaw !== undefined && genderRaw !== null) {
    gender = Array.isArray(genderRaw) ? Number(genderRaw[0]) : Number(genderRaw);
    if (!Number.isFinite(gender)) {
      gender = null;
    }
  }

  return {
    country: filters.country?.[0] ?? null,
    gender,
    rating: filters.rating ?? null,
    ageFrom: typeof filters.ageFrom === 'number' ? filters.ageFrom : null,
    ageTo: typeof filters.ageTo === 'number' ? filters.ageTo : null,
    isVerified: filters.isVerified === true,
    hasReviews: filters.hasReviews === true,
    tikTokFollowersFrom: filters.tikTokFollowersFrom ?? null,
    instagramFollowersFrom: filters.instagramFollowersFrom ?? null,
    tikTokPerformance: assignmentPerformanceFromDto(filters, 'tikTok'),
    instagramPerformance: assignmentPerformanceFromDto(filters, 'instagram'),
    youtubePerformance: assignmentPerformanceFromDto(filters, 'youtube'),
    screening: { ...screening },
  };
}

function applyAssignmentPerformanceToDto(
  dto: FilterCreatorsDto,
  draft: PlatformPerformanceDraft,
  prefix: 'tikTok' | 'instagram' | 'youtube',
): void {
  if (draft.averageEngagement != null) {
    (dto as Record<string, number>)[`${prefix}AverageEngagementFrom`] = draft.averageEngagement;
  }
  applyAssignmentRange(dto, draft.averageViews, `${prefix}AverageViewsFrom`, `${prefix}AverageViewsTo`);
  applyAssignmentRange(dto, draft.totalViews, `${prefix}TotalViewsFrom`, `${prefix}TotalViewsTo`);
  applyAssignmentRange(dto, draft.averageLikes, `${prefix}AverageLikesFrom`, `${prefix}AverageLikesTo`);
  applyAssignmentRange(dto, draft.totalLikes, `${prefix}TotalLikesFrom`, `${prefix}TotalLikesTo`);
}

function applyAssignmentRange(
  dto: FilterCreatorsDto,
  range: PerformanceRangeValue | null,
  fromKey: string,
  toKey: string,
): void {
  if (!range || isDefaultPerformanceRange(range)) {
    return;
  }
  (dto as Record<string, number>)[fromKey] = range.min;
  (dto as Record<string, number>)[toKey] = range.max;
}

function assignmentPerformanceFromDto(
  filters: FilterCreatorsDto,
  prefix: 'tikTok' | 'instagram' | 'youtube',
): PlatformPerformanceDraft {
  const record = filters as Record<string, number | null | undefined>;
  return {
    averageEngagement: typeof record[`${prefix}AverageEngagementFrom`] === 'number'
      ? record[`${prefix}AverageEngagementFrom`] as number
      : null,
    averageViews: assignmentRangeFromDto(record[`${prefix}AverageViewsFrom`], record[`${prefix}AverageViewsTo`]),
    totalViews: assignmentRangeFromDto(record[`${prefix}TotalViewsFrom`], record[`${prefix}TotalViewsTo`]),
    averageLikes: assignmentRangeFromDto(record[`${prefix}AverageLikesFrom`], record[`${prefix}AverageLikesTo`]),
    totalLikes: assignmentRangeFromDto(record[`${prefix}TotalLikesFrom`], record[`${prefix}TotalLikesTo`]),
  };
}

function assignmentRangeFromDto(
  from?: number | null,
  to?: number | null,
): PerformanceRangeValue | null {
  if (from == null && to == null) {
    return null;
  }
  const range: PerformanceRangeValue = {
    min: from ?? PERFORMANCE_RANGE_DEFAULT.min,
    max: to ?? PERFORMANCE_RANGE_DEFAULT.max,
  };
  return isDefaultPerformanceRange(range) ? null : range;
}

export function screeningQuestionFilterOptions(
  type: ScreeningQuestionTypeEnum | undefined,
  hasScorableExpected: boolean,
): { value: string; label: string }[] {
  if (type === ScreeningQuestionTypeEnum.YesNo) {
    return [
      { value: 'Yes', label: 'Yes' },
      { value: 'No', label: 'No' },
    ];
  }
  if (hasScorableExpected) {
    return [
      { value: 'pass', label: 'Pass' },
      { value: 'fail', label: 'Fail' },
    ];
  }
  return [{ value: 'answered', label: 'Answered' }];
}

export function truncateQuestionLabel(question: string, maxLen = 36): string {
  const trimmed = question.trim();
  if (trimmed.length <= maxLen) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLen - 1)}…`;
}
